import { useCallback, useState } from 'react'
import { postAcceptance, TermsChangedError } from './api.js'

export type AcceptStatus = 'idle' | 'submitting' | 'success' | 'changed' | 'error'

export interface UseAcceptTermsOptions {
  /** Called after a successful acceptance. */
  onSuccess?: () => void
  /** Called when the server reports the version changed (409) — re-review is needed. */
  onChanged?: () => void
}

export interface UseAcceptTerms {
  status: AcceptStatus
  error: string | null
  accept: (version: number) => void
}

/**
 * Submits the org's acceptance of a version. Surfaces a distinct `changed` status on
 * a 409 (the applicable version moved on while the screen was open) so the UI can ask
 * the respondent to re-read the new terms rather than showing a generic error.
 */
export function useAcceptTerms(
  organizationSurveyId: string,
  { onSuccess, onChanged }: UseAcceptTermsOptions = {},
): UseAcceptTerms {
  const [status, setStatus] = useState<AcceptStatus>('idle')
  const [error, setError] = useState<string | null>(null)

  const accept = useCallback(
    (version: number) => {
      setStatus('submitting')
      setError(null)
      void (async () => {
        try {
          await postAcceptance(organizationSurveyId, version)
          setStatus('success')
          onSuccess?.()
        } catch (err: unknown) {
          if (err instanceof TermsChangedError) {
            setStatus('changed')
            setError(err.message)
            onChanged?.()
            return
          }
          setStatus('error')
          setError(err instanceof Error ? err.message : String(err))
        }
      })()
    },
    [organizationSurveyId, onSuccess, onChanged],
  )

  return { status, error, accept }
}
